import React, { useState, useEffect } from 'react';
import { 
  adminInspectionService, 
  adminInspectorService, 
  adminPricingService 
} from '../../services/adminService';
import InspectionDetailsModal from './workflow/InspectionDetailsModal';

const InspectionsManagement = () => {
  const [inspections, setInspections] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInspections();
    loadInspectors();
  }, []);

  const loadInspections = async () => {
    try {
      setLoading(true);
      const response = await adminInspectionService.getAllInspections();
      console.log('API Response:', response);
      setInspections(response.car_inspections || []);
    } catch (error) {
      console.error('Error loading inspections:', error);
      alert('Failed to load inspections: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadInspectors = async () => {
    try {
      const response = await adminInspectorService.getAllInspectors();
      setInspectors(response || []);
    } catch (error) {
      console.error('Error loading inspectors:', error);
    }
  };

  const handleViewDetails = (inspection) => {
    setSelectedInspection(inspection);
    setModalType('details');
    setShowModal(true);
  };

  const handleAssignInspector = (inspection) => {
    setSelectedInspection(inspection);
    setModalType('assign');
    setShowModal(true);
  };

  const handleSetPricing = (inspection) => {
    setSelectedInspection(inspection);
    setModalType('pricing');
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: '#ffc107',
      scheduled: '#17a2b8',
      completed: '#28a745',
      priced: '#6f42c1',
      confirmed: '#20c997',
      cancelled: '#dc3545',
      approved: '#28a745'
    };
    return (
      <span 
        className="status-badge"
        style={{ background: `${statusColors[status]}20`, color: statusColors[status] }}
      >
        {status}
      </span>
    );
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesFilter = filterStatus === 'all' || inspection.status === filterStatus;
    const matchesSearch = 
      inspection.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.sell_request?.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.sell_request?.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.id?.toString().includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="management-loading">
        <div className="spinner-large"></div>
        <p>Loading inspections...</p>
      </div>
    );
  }

  return (
    <div className="inspections-management">
      <div className="management-header">
        <div>
          <h1>🔍 Inspections Management</h1>
          <p>View inspection reports, assign inspectors, and set pricing</p>
        </div>
        <button className="refresh-btn" onClick={loadInspections}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.2 3.7 15.4 2.8 13.5 2.3C9.5 1.3 5.5 2.9 3.5 6.3C1.5 9.7 1.8 14 4.5 17M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.8 20.3 8.6 21.2 10.5 21.7C14.5 22.7 18.5 21.1 20.5 17.7C22.5 14.3 22.2 10 19.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="management-filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by ID, booking ref, or car..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-tabs">
          {['all', 'pending', 'scheduled', 'completed', 'priced', 'confirmed', 'cancelled'].map((status) => (
            <button
              key={status}
              className={`filter-tab ${filterStatus === status ? 'active' : ''}`}
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              <span className="filter-count">
                {status === 'all' 
                  ? inspections.length 
                  : inspections.filter(i => i.status === status).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Inspections Table */}
      <div className="management-table-wrapper" style={{ overflowX: 'auto' }}>
        <div className="management-table">
          <table style={{ tableLayout: 'auto', width: '100%', minWidth: '900px' }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>ID</th>
                <th style={{ whiteSpace: 'nowrap' }}>Booking Ref</th>
                <th>Car</th>
                <th>Seller</th>
                <th>Inspector</th>
                <th>Type</th>
                <th>Location</th>
                <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                <th>Score</th>
                <th>Price</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInspections.map((inspection) => (
                <tr key={inspection.id}>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>#{inspection.id}</strong></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <code style={{ fontSize: '10px' }}>{inspection.booking_reference}</code>
                  </td>
                  <td>
                    <div className="car-details">
                      <h4>{inspection.sell_request?.brand} {inspection.sell_request?.model}</h4>
                      <p>{inspection.sell_request?.year} • {inspection.sell_request?.km_driven?.toLocaleString()} km</p>
                    </div>
                  </td>
                  <td>
                    <div>
                      <p style={{ fontSize: '11px', margin: 0 }}>{inspection.seller?.name || 'N/A'}</p>
                      <small style={{ fontSize: '10px', color: '#666' }}>{inspection.seller?.phone}</small>
                    </div>
                  </td>
                  <td>
                    {inspection.inspector ? (
                      <div>
                        <p style={{ fontSize: '11px', margin: 0 }}>{inspection.inspector.name}</p>
                        <small style={{ fontSize: '10px', color: '#666' }}>ID: {inspection.inspector.id}</small>
                      </div>
                    ) : (
                      <span style={{ color: '#dc3545', fontSize: '11px', whiteSpace: 'nowrap' }}>Not Assigned</span>
                    )}
                  </td>
                  <td style={{ textTransform: 'capitalize', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {inspection.inspection_type || 'Standard'}
                  </td>
                  <td>
                    <div>
                      <p style={{ fontSize: '11px', margin: 0, whiteSpace: 'nowrap' }}>📍 {inspection.city}</p>
                      {inspection.inspection_type === 'branch' && (
                        <small style={{ fontSize: '10px', color: '#666' }}>{inspection.branch_location}</small>
                      )}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div>
                      <p style={{ fontSize: '11px', margin: 0 }}>
                        {inspection.preferred_date ? new Date(inspection.preferred_date).toLocaleDateString() : 'N/A'}
                      </p>
                      <small style={{ textTransform: 'capitalize', fontSize: '10px', color: '#666' }}>
                        {inspection.preferred_time || 'N/A'}
                      </small>
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inspection.inspection_report ? (
                      <div>
                        <strong style={{ color: '#28a745', fontSize: '13px' }}>
                          {inspection.inspection_report.overall_score}/10
                        </strong>
                        {inspection.inspection_report.flooded && (
                          <div><small style={{ color: '#dc3545', fontSize: '10px' }}>⚠️ Flood</small></div>
                        )}
                        {inspection.inspection_report.meter_tampered && (
                          <div><small style={{ color: '#dc3545', fontSize: '10px' }}>⚠️ Tamp.</small></div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#999' }}>—</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inspection.estimated_price ? (
                      <strong style={{ color: '#28a745', fontSize: '11px' }}>
                        ₹{inspection.estimated_price.toLocaleString()}
                      </strong>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#999' }}>—</span>
                    )}
                  </td>
                  <td>{getStatusBadge(inspection.status)}</td>
                  <td>
                    {inspection.payments && inspection.payments.length > 0 ? (
                      <div>
                        <span 
                          className="status-badge" 
                          style={{ 
                            fontSize: '10px',
                            background: inspection.payments[0].status === 'success' ? '#28a74520' : '#ffc10720',
                            color: inspection.payments[0].status === 'success' ? '#28a745' : '#ffc107'
                          }}
                        >
                          {inspection.payments[0].status}
                        </span>
                        <small style={{ display: 'block', marginTop: '2px', fontSize: '10px' }}>
                          ₹{(inspection.payments[0].amount_cents / 100).toFixed(0)}
                        </small>
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#999' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-view"
                        onClick={() => handleViewDetails(inspection)}
                        title="View Full Report"
                      >
                        👁️
                      </button>
                      {['pending', 'confirmed'].includes(inspection.status) && !inspection.inspector && (
                        <button 
                          className="btn-approve"
                          onClick={() => handleAssignInspector(inspection)}
                          title="Assign Inspector"
                        >
                          👤
                        </button>
                      )}
                      {inspection.status === 'completed' && inspection.inspection_report && !inspection.estimated_price && (
                        <button 
                          className="btn-approve"
                          onClick={() => handleSetPricing(inspection)}
                          title="Set Pricing"
                        >
                          💰
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredInspections.length === 0 && (
            <div className="no-results">
              <p>No inspections found</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && selectedInspection && (
        <>
          {modalType === 'details' && (
            <InspectionDetailsModal
              inspection={selectedInspection}
              onClose={() => {
                setShowModal(false);
                setSelectedInspection(null);
                setModalType('');
              }}
            />
          )}
          {modalType === 'assign' && (
            <AssignInspectorModal
              inspection={selectedInspection}
              inspectors={inspectors}
              onClose={() => {
                setShowModal(false);
                setSelectedInspection(null);
                setModalType('');
              }}
              onSuccess={() => {
                setShowModal(false);
                setSelectedInspection(null);
                setModalType('');
                loadInspections();
              }}
            />
          )}
          {modalType === 'pricing' && (
            <SetPricingModal
              inspection={selectedInspection}
              onClose={() => {
                setShowModal(false);
                setSelectedInspection(null);
                setModalType('');
              }}
              onSuccess={() => {
                setShowModal(false);
                setSelectedInspection(null);
                setModalType('');
                loadInspections();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Assign Inspector Modal Component
const AssignInspectorModal = ({ inspection, inspectors, onClose, onSuccess }) => {
  const [selectedInspectorId, setSelectedInspectorId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedInspectorId) {
      alert('Please select an inspector');
      return;
    }

    try {
      setSubmitting(true);
      await adminInspectionService.assignInspector(inspection.id, selectedInspectorId);
      alert('✅ Inspector assigned successfully!');
      onSuccess();
    } catch (error) {
      alert('Failed to assign inspector: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👤 Assign Inspector</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="inspection-summary">
            <h3>Inspection #{inspection.id}</h3>
            <p>{inspection.sell_request?.brand} {inspection.sell_request?.model} {inspection.sell_request?.year}</p>
            <p>📍 {inspection.city} • 📅 {new Date(inspection.created_at).toLocaleDateString()}</p>
          </div>

          <form onSubmit={handleSubmit} className="assign-form">
            <div className="form-group">
              <label>Select Inspector *</label>
              <select 
                className="form-select"
                value={selectedInspectorId}
                onChange={(e) => setSelectedInspectorId(e.target.value)}
                required
              >
                <option value="">Choose an inspector</option>
                {inspectors.map(inspector => (
                  <option key={inspector.id} value={inspector.id}>
                    {inspector.name} ({inspector.email})
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button 
            className="btn-modal-cancel" 
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn-modal-submit"
            onClick={handleSubmit}
            disabled={submitting || !selectedInspectorId}
          >
            {submitting ? '⏳ Assigning...' : '👤 Assign Inspector'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Set Pricing Modal Component
const SetPricingModal = ({ inspection, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    selling_price: '',
    condition_grade: '',
    price_reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.selling_price || !formData.condition_grade || !formData.price_reason) {
      alert('Please fill all fields');
      return;
    }

    try {
      setSubmitting(true);
      await adminPricingService.createPricing(inspection.id, formData);
      alert('✅ Pricing submitted successfully! Seller will be notified.');
      onSuccess();
    } catch (error) {
      alert('Failed to submit pricing: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>💰 Set Pricing - Inspection #{inspection.id}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="pricing-summary">
            <h3>
              {inspection.sell_request?.brand} {inspection.sell_request?.model} {inspection.sell_request?.year}
            </h3>
            <p>📍 {inspection.city} • 🛣️ {inspection.sell_request?.km_driven?.toLocaleString()} km</p>
            
            {inspection.inspection_report && (
              <div className="report-quick-view">
                <span className="score-badge">
                  Score: {inspection.inspection_report.overall_score}/10
                </span>
                {!inspection.inspection_report.flooded && (
                  <span className="success-badge">✅ Not Flooded</span>
                )}
                {!inspection.inspection_report.meter_tampered && (
                  <span className="success-badge">✅ Meter OK</span>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="pricing-form">
            <div className="form-group">
              <label>Selling Price (₹) *</label>
              <input
                type="number"
                value={formData.selling_price}
                onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                placeholder="Enter price in rupees"
                className="form-input"
                required
              />
              <small>Set competitive price based on inspection report</small>
            </div>

            <div className="form-group">
              <label>Condition Grade *</label>
              <select
                value={formData.condition_grade}
                onChange={(e) => setFormData({ ...formData, condition_grade: e.target.value })}
                className="form-select"
                required
              >
                <option value="">Select Grade</option>
                <option value="Excellent">Excellent (9-10/10)</option>
                <option value="Good">Good (7-8/10)</option>
                <option value="Fair">Fair (5-6/10)</option>
                <option value="Poor">Poor (Below 5/10)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Price Justification *</label>
              <textarea
                value={formData.price_reason}
                onChange={(e) => setFormData({ ...formData, price_reason: e.target.value })}
                placeholder="Explain pricing rationale based on inspection findings..."
                rows="5"
                className="form-textarea"
                required
              />
              <small>This will be sent to the seller</small>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-modal-cancel" 
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-modal-submit"
            onClick={handleSubmit}
            disabled={submitting || !formData.selling_price || !formData.condition_grade || !formData.price_reason}
          >
            {submitting ? '⏳ Submitting...' : '💰 Submit Pricing'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionsManagement;